import {Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild, ChangeDetectorRef} from '@angular/core';
import {Router} from '@angular/router';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {Dictionary, head, isEmpty} from 'lodash';
import {SCREEN_LAYOUT, ScreenLayout} from '../core/layout/layout.provider';
import {DataService} from '../data/data.service';
import {Product, Settings, Transaction, TransactionHistoryItem, User} from '../data/models';
import {APP_SETTINGS} from '../data/settings-data.provider';
import {BehaviorSubject, Observable, pairwise, scan, Subject, takeUntil} from 'rxjs';
import {InfiniteScrollComponent} from '../products/infinite-scroll.component';
import { ProductReturnsService } from '../product-returns/product-returns.service';
import { PrinterService } from '../data/printer.service';

export interface TransactionHistoryItemViewModel extends TransactionHistoryItem {
  productImage: string;
  returned_quantity?: number;
}

export interface TransactionViewModel extends Transaction {
  userAvatar: string;
  items: TransactionHistoryItemViewModel[];
  paymentType?: string;
}

interface TransactionHistoryLoadingOptions {
  page: number;
  filter: string;
}

@Component({
    selector: 'app-transaction-history-page',
    styles: [`
    :host {
      display: flex;
      width: 100%;
      height: 100%;
      flex: 1 1 auto;
      flex-direction: column;
      flex-wrap: nowrap;
    }
  `],
    template: `
    <ng-container *ngIf="layout === 'desktop'">
      <div class="modal-header">
        <h4 class="modal-title" id="exampleModalLabel" i18n>Verkaufshistorie</h4>
        <button type="button" class="btn-close" aria-label="Close" i18n-aria-label
                (click)="modalService.dismissAll()"></button>
      </div>
      <div class="modal-body d-flex p-0" [appLoadingOverlay]="loading">
        <!-- History sidebar -->
        <div class="flex-column border-end col-md-4 d-flex">
          <app-transaction-history-searchbar (searchChange)="handLeTransactionSearch($event)" class="border-bottom"></app-transaction-history-searchbar>

          <app-infinite-scroll class="px-3 px-sm-3 px-md-3 px-lg-4 pt-2 flex-grow-1 flex-shrink-1 overflow-y-auto" (scrolled)="loadNextPage()">
            <div class="list-group rounded-top-0">
              <app-transaction-history-sidebar-item
                class= "cursor-pointer list-group-item list-group-item-action border-bottom border-top-0 border-start-0 border-end-0"
                [class.list-group-item-info]="selectedTransaction?.id === transaction.id"
                *ngFor="let transaction of transactionHistory; trackBy: trackByTransactionId"
                (click)="handleSidebarItemSelection(transaction)"
                [transaction]="transaction">
              </app-transaction-history-sidebar-item>
            </div>
          </app-infinite-scroll>
        </div>

        <!-- History content -->
        <app-transaction-history-item
          class="flex-column flex-grow-1 d-flex"
          [transaction]="selectedTransaction"
          [allReturned]="allReturned"
          (productReturns)="goToProductReturns($event)">
        </app-transaction-history-item>
      </div>
    </ng-container>

    <!-- Mobile Layout -->
    <!-- History List view -->
    <ng-container *ngIf="layout === 'mobile' && activeView === 'list'">
      <div class="top"></div>
      <h2 i18n class="d-flex bg-white mb-0 ps-3 pt-3 le-3">Verkaufshistorie</h2>
      <div class="d-flex flex-column flex-nowrap bg-white flex-shrink-1 h-25 flex-grow-1 bottom" [appLoadingOverlay]="loading">
        <app-transaction-history-searchbar
          (searchChange)="handLeTransactionSearch($event)"
          class="border-bottom bg-white"></app-transaction-history-searchbar>

        <app-infinite-scroll class="px-3 px-sm-3 px-md-3 px-lg-4 pt-2 flex-grow-1 flex-shrink-1 overflow-y-auto" (scrolled)="loadNextPage()">
          <div class="flex-grow-1 flex-shrink-1 list-group rounded-0">
            <app-transaction-history-sidebar-item
              class="cursor-pointer list-group-item list-group-item-action border-bottom border-top-0 border-start-0 border-end-0"
              [class.list-group-item-info]="selectedTransaction?.id === transaction.id"
              *ngFor="let transaction of transactionHistory; trackBy: trackByTransactionId"
              (click)="handleSidebarItemSelection(transaction)"
              [transaction]="transaction">
            </app-transaction-history-sidebar-item>
          </div>
        </app-infinite-scroll>
      </div>
    </ng-container>

    <!-- History Details view -->
    <ng-container *ngIf="layout === 'mobile' && activeView === 'details'">
      <div class="bg-white d-flex flex-column flex-nowrap bg-white flex-shrink-1 h-25 flex-grow-1 top bottom">
        <div class="d-flex bg-white align-items-center justify-content-between">
          <div class="d-flex justify-content-start align-items-center p-3">
            <button type="button" class="btn btn-outline-info rounded-circle border btn-lg" (click)="activeView = 'list'">
              <span class="material-icons fw-bold">arrow_back</span>
            </button>
            <div class="ps-3">
              <span class="text-muted fs-3" i18n>Verkauf:</span>
              <span class="fw-bolder ps-2 fs-3">#{{selectedTransaction?.transactionNumber}}</span>
            </div>
          </div>
          <span class="ps-4 pe-3 text-muted">{{selectedTransaction | formatDate}} {{selectedTransaction | formatTime}} Uhr
            ({{selectedTransaction?.createdAt | dateFromNow}})</span>
        </div>
        <div class="d-flex bg-white justify-content-between ps-3 pe-3 pb-3">
          <div class="flex-column d-flex">
            <span class="fw-bold fs-4" i18n>{{selectedTransaction?.items?.length}} Artikel</span>
            <span class="">{{selectedTransaction?.userName}}</span>
          </div>
          <div class="flex-column d-flex align-items-end">
            <span class="flex-row fs-5 text-info fw-bold">{{selectedTransaction?.total | currency}}</span>
            <span class="flex-row text-muted">{{selectedTransaction?.taxTotal | currency }} MWSt</span>
          </div>
        </div>
        <div class="ps-3 pe-3 d-flex flex-column overflow-y-auto flex-grow-1 flex-shrink-1 h-100">
          <app-transaction-history-item-article *ngFor="let item of selectedTransaction?.items" [transactionItem]="item">
          </app-transaction-history-item-article>
        </div>
        <div class="flex-grow-0 flex-shrink-0 d-flex flex-column m-4 align-items-center">
          <ng-container *ngIf="showPrintButton">
            <button
              *ngIf="!isSPOS"
              type="button"
              class="btn btn-info btn-lg text-white w-75 mb-2"
              [appLoadingOverlay]="printing"
              (click)="printReceipt()"
              i18n
            >
              <span class="d-flex align-items-center justify-content-center">
                <span class="material-icons me-1">print</span>
                <span>Beleg drucken</span>
              </span>
            </button>

            <ng-container *ngIf="isSPOS">
              <button type="button" class="btn btn-info btn-lg text-white w-75 mb-2" [appLoadingOverlay]="printing" (click)="printReceipt()">
                <span class="d-flex align-items-center justify-content-center">
                  <span class="material-icons me-1">print</span>
                  <span i18n>Kundenbeleg drucken</span>
                </span>
              </button>
              <button type="button" class="btn btn-info btn-lg text-white w-75 mb-2" [appLoadingOverlay]="printing" (click)="printReceipt(true)">
                <span class="d-flex align-items-center justify-content-center">
                  <span class="material-icons me-1">print</span>
                  <span i18n>Händlerbeleg drucken</span>
                </span>
              </button>
            </ng-container>
          </ng-container>
          <button class="btn btn-info btn-lg text-white w-75" i18n [disabled]="allReturned"
                  (click)="goToProductReturns(selectedTransaction)">{{ allReturned ? 'Vollständig zurückgegeben' : 'Warenrücknahme' }}
          </button>
        </div>
      </div>
    </ng-container>
  `,
    standalone: false
})
export class TransactionHistoryPage implements OnInit, OnDestroy {

  @ViewChild(InfiniteScrollComponent, { read: ElementRef }) scrollContainer!: ElementRef;

  transactionHistory: Transaction[] = [];
  selectedTransaction: TransactionViewModel | null = null;
  products: Product[] = [];
  productImages: Dictionary<string> = {};
  userAvatars: Dictionary<string> = {};

  activeView: 'list' | 'details' = 'list';

  loading = false;
  pageCount = 1;
  initialFetch = true;
  allReturned = false;
  printing = false;

  private _transactionHistoryLoadingOptions = new BehaviorSubject<Partial<TransactionHistoryLoadingOptions>>({
    filter: '',
    page: 0
  });

  constructor(
    private readonly dataService: DataService,
    public readonly modalService: NgbModal,
    @Inject(APP_SETTINGS) private readonly settings: Settings,
    @Inject(SCREEN_LAYOUT) public layout: ScreenLayout,
    private readonly router: Router,
    public productReturnsService: ProductReturnsService, // Inject the service
    private cdr: ChangeDetectorRef,
    public printerService: PrinterService
  ) { }

  destroy = new Subject<void>();

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  async ngOnInit(): Promise<void> {

    const transactionHistoryLoadingOptionChanges: Observable<TransactionHistoryLoadingOptions> = this._transactionHistoryLoadingOptions.pipe(
      scan((acc, curr) => Object.assign({}, acc, curr), {} as TransactionHistoryLoadingOptions)
    );

    transactionHistoryLoadingOptionChanges.pipe(
      pairwise(),
      takeUntil(this.destroy)
    ).subscribe(async ([previousOption, currentOption]) => {

      try {
        this.loading = true;
        if (this.initialFetch) {
          this.products = await this.fetchProducts();
          this.productImages = this.products.reduce((acc: Dictionary<string>, product: Product) => {
            const firstImage = head(head(product.variants)?.images)?.url || null;
            return { ...acc, [product.id]: firstImage };
          }, {});
          this.userAvatars = this.settings.users.reduce((acc: Dictionary<string>, user: User) => {
            return { ...acc, [user.name]: user.gravatarUrl };
          }, {});
        }
        const transactionViewModels = await this.fetchTransactionHistory(currentOption.filter, currentOption.page);
        const uniqueTransactions = Array.from(
          new Map(transactionViewModels.map(transaction => [transaction.id, transaction])).values()
        );
        if (currentOption.filter !== previousOption.filter) {
          // Filters changed, reset items
          this.transactionHistory = uniqueTransactions;
          this.scrollContainer.nativeElement.scrollTop = 0;
        } else {
          // Filters unchanged, add items to list
          this.transactionHistory.push(...uniqueTransactions);
        }
      }
      finally {
        this.loading = false;
      }
      if (this.initialFetch) {
        this.selectFirst();
        this.initialFetch = false;
      }
    });
  }

  async fetchTransactionHistory(search: string, page: number) {
    const transactionHistory = await this.dataService.fetchPaginatedTransactionHistory({ p: page, search: search });
    this.pageCount = transactionHistory.meta.total;
    return transactionHistory.transactions;
  }

  async fetchProducts() {
    try {
      return await this.dataService.fetchAllProducts();
    } finally { }
  }

  toTransactionViewModel(transaction: Transaction): TransactionViewModel {
    return {
      ...transaction,
      userAvatar: this.userAvatars[transaction.userName],
      items: transaction.items.map(item => ({ ...item, productImage: this.productImages[item.productId] }))
    };
  }

  async handleSidebarItemSelection(transaction: Transaction) {
    this.loading = true;
    const viewModel = this.toTransactionViewModel(transaction);
    await this.fetchAdditionalData(viewModel);
    this.selectedTransaction = viewModel;
    this.checkAllItemsReturned();
    this.loading = false;
    this.cdr.markForCheck(); // Ensure Angular detects changes

    if (this.layout === ScreenLayout.mobile) {
      this.activeView = 'details';
    }
  }

  async selectFirst() {
    if (isEmpty(this.transactionHistory)) {
      return;
    }

    const selectedTransaction = head(this.transactionHistory);
    if (selectedTransaction) {
      this.loading = true;
      const viewModel = this.toTransactionViewModel(selectedTransaction);
      await this.fetchAdditionalData(viewModel);
      this.selectedTransaction = viewModel;
      this.checkAllItemsReturned();
      this.loading = false;
      this.cdr.markForCheck(); // Ensure Angular detects changes
    }
  }

  private async fetchAdditionalData(transaction: TransactionViewModel): Promise<void> {
    try {
      await this.productReturnsService.initContent(transaction.id);

      transaction.items = transaction.items.map((item, index) => ({
        ...item,
        returned_quantity: this.productReturnsService.productReturnItems[index]?.returnedQuantity || 0,
      }));
    } catch (error) {
      console.error(`Error fetching additional data for transaction ID: ${transaction.id}`, error);
    }
  }

  private checkAllItemsReturned(): void {
    if (this.selectedTransaction && this.selectedTransaction.items) {
      const allReturned = this.selectedTransaction.items.every(item => (item.returned_quantity || 0) >= item.quantity);
      this.allReturned = allReturned;
    }
  }

  handLeTransactionSearch(search: string | null) {
    this._transactionHistoryLoadingOptions.next({filter: search ?? '', page: 1});
  }

  goToProductReturns<T extends Transaction>(transaction: T | null) {
    if (!transaction) {
      return;
    }

    this.router.navigate(['/returns', transaction.id]);
    this.modalService.dismissAll();
  }

  loadNextPage() {
    const currentPage = this._transactionHistoryLoadingOptions.getValue().page ?? 1;
    if (currentPage < this.pageCount) {
      this._transactionHistoryLoadingOptions.next({page: currentPage + 1});
    }
  }

  trackByTransactionId(index: number, item: Transaction): number {
    return item.id; // or any unique identifier
  }

  get showPrintButton() {
    const { receiptUrl, receiptImageUrl, receiptEposUrl } = this.selectedTransaction ?? {};

    return receiptUrl && receiptImageUrl && receiptEposUrl;
  }

  get isSPOS() {
    return this.selectedTransaction?.paymentType === 'versa_commerce_pos_spos';
  }

  printReceipt(isMerchant = false) {
    if (!this.selectedTransaction) {
      return;
    }

    this.printing = true;

    const { receiptUrl, receiptImageUrl, receiptEposUrl } = this.selectedTransaction;

    this.printerService.printReceipt(receiptUrl, receiptImageUrl, receiptEposUrl, isMerchant);

    this.printing = false;
  }
}
